import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import Marquee from "@/components/magicui/marquee";
import EmailDemoInput from "@/components/marketing/pieces/EmailDemoInput";
import { cn } from "@/lib/utils";
import { withIkVideoQuality } from "@/lib/utils/marketing";

export interface HeroCard {
  name: string;
  company: string;
  image: string;
  headline?: string;
  stat?: string;
  statLabel?: string;
  ctaLink?: string;
}

interface HeroSectionContent {
  badgeSegments: string[];
  heading: React.ReactNode;
  description: React.ReactNode;
  socialProof: {
    rating: string;
    ratingLabel: string;
    subtext: string;
  };
  trustLine: React.ReactNode;
  backgroundVideo?: string;
  backgroundImage?: string;
}

interface HeroSectionProps {
  cards: HeroCard[];
  content: HeroSectionContent;
}

function HeroCardItem({ card }: { card: HeroCard }) {
  return (
    <Link href={card.ctaLink ?? ""}>
      <div className="relative flex flex-col justify-between w-37 md:w-64 h-55 md:h-80 rounded-xl md:rounded-3xl p-4 md:p-6 overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-ash-800" />
        <Image src={card.image} alt={card.name} fill className="absolute inset-0 object-cover opacity-20" />
        <div className="relative z-10 flex flex-col justify-between h-full">
          {card.stat ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-white font-extrabold text-3xl md:text-4xl leading-none tracking-tight">
                  {card.stat}
                </span>
                <span className="text-clay-500 text-sm">{card.statLabel}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-white font-medium text-xs">{card.name}</span>
                <span className="text-white/55 text-xs">{card.company}</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-white font-semibold text-sm">{card.name}</span>
                <span className="text-white/55 text-xs">{card.company}</span>
              </div>
              <p className="text-white font-semibold text-sm md:text-xl leading-[1.3] tracking-tight">
                {card.headline}
              </p>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function HeroSection({ cards, content }: HeroSectionProps) {
  const { backgroundVideo, backgroundImage } = content;
  const { calPath } = useCalendarLink();
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isInView) return;
    video.play().catch(() => {});
  }, [isInView]);

  return (
    <section ref={sectionRef} className="relative 0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, #242424 0%, #171717 100%)",
        }}
      />

      {backgroundImage && (
        <Image
          src={backgroundImage}
          alt=""
          fill
          priority
          className={cn(
            "absolute inset-0 object-cover transition-opacity duration-1000 opacity-5",
            isVideoReady && "opacity-0"
          )}
        />
      )}

      {backgroundVideo && isInView && (
        <video
          ref={videoRef}
          src={withIkVideoQuality(backgroundVideo)}
          loop
          muted
          playsInline
          onCanPlay={() => setIsVideoReady(true)}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-1000",
            isVideoReady ? "opacity-5" : "opacity-0"
          )}
        />
      )}

      <div className="relative flex flex-col gap-15 md:gap-20 items-center justify-between pt-40 md:pt-54 pb-6">
        <div className="flex flex-col items-center gap-6 md:gap-12 px-5 w-full max-w-screen-3xl mx-auto">
          <div className="flex flex-col items-center gap-4 md:gap-6 w-full">
            <div className="flex flex-col items-center gap-4 w-full">
              <div className="flex items-center gap-2 md:gap-3 px-2 py-1 md:px-4 md:py-3 rounded-full border border-clay-400/30 backdrop-blur-sm bg-black/10">
                {content.badgeSegments.map((segment, i) => (
                  <span key={i} className="contents">
                    {i > 0 && <span className="w-1 h-1 rounded-sm bg-clay-500" />}
                    <span className="text-[9px] md:text-xs font-bold uppercase tracking-[1px] text-clay-500">
                      {segment}
                    </span>
                  </span>
                ))}
              </div>

              <h1 className="font-clash font-medium text-4xl md:text-5xl lg:text-7xl leading-none tracking-[-1.6px] text-center text-clay-100">
                {content.heading}
              </h1>

              <p className="text-sm md:text-base lg:text-lg text-clay-200 text-center max-w-[780px] leading-relaxed">
                {content.description}
              </p>
            </div>

            <EmailDemoInput calPath={calPath} />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-5 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-honey-400 text-base tracking-[1px]">★★★★★</span>
              <span className="text-clay-400">
                <span className="font-bold">{content.socialProof.rating}</span>
                <span className="text-clay-500"> {content.socialProof.ratingLabel}</span>
              </span>
            </div>
            <div className="w-px h-5 bg-clay-700 hidden md:block" />
            <span className="text-clay-500 text-sm">{content.socialProof.subtext}</span>
          </div>
        </div>

        <div className="w-full flex flex-col gap-5 md:gap-6">
          <div className="pl-4 md:pl-8 lg:pl-28 max-w-screen-3xl">
            <p className="text-sm md:text-lg text-clay-300">{content.trustLine}</p>
          </div>
          <Marquee className="w-full [--duration:60s] [--gap:12px] md:[--gap:24px] p-0" pauseOnHover>
            {cards.map((card, i) => (
              <HeroCardItem key={i} card={card} />
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
}
